import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { ConvenienceDispatchFixedRouteEntity } from 'src/entities/ConvenienceDispatchFixedRoute.entity';

const BASE_PATH = '/api/v5/convenience_dispatches/fixed_routes';

// 固定ルート新規保存: 対象日の現在配車状態 → 新規 fixed route
export const useSaveFixedRouteSnapshotMutation = () =>
  useMutation<ConvenienceDispatchFixedRouteEntity, Error, { planningDate: string; name: string }>({
    mutationFn: async ({ planningDate, name }) => {
      const res = await axios.post<{ id: number; name: string; status: string }>(
        `${BASE_PATH}/snapshots`,
        { data: { planning_date: planningDate, name } }
      );
      return {
        id: res.data.id,
        name: res.data.name,
        status: res.data.status as 'draft' | 'active' | 'archived',
        description: null,
        vehicleCount: 0,
        updatedAt: new Date().toISOString(),
      };
    },
  });

// 固定ルート上書き保存: 対象日の現在配車状態 → 既存 fixed route を上書き
export const useOverwriteFixedRouteSnapshotMutation = () =>
  useMutation<ConvenienceDispatchFixedRouteEntity, Error, { planningDate: string; fixedRouteId: number }>({
    mutationFn: async ({ planningDate, fixedRouteId }) => {
      const res = await axios.patch<{ id: number; name: string; status: string }>(
        `${BASE_PATH}/${fixedRouteId}/snapshots`,
        { data: { planning_date: planningDate } }
      );
      return {
        id: res.data.id,
        name: res.data.name,
        status: res.data.status as 'draft' | 'active' | 'archived',
        description: null,
        vehicleCount: 0,
        updatedAt: new Date().toISOString(),
      };
    },
  });

export type ApplyFixedRouteResult = {
  planningDate: string;
  fixedRouteId: number;
  applied: boolean;
  assignedCount: number;
  unassignedCount: number;
  noDeliveryCount: number;
};

// 固定ルート名前変更
export const useRenameFixedRouteMutation = () =>
  useMutation<ConvenienceDispatchFixedRouteEntity, Error, { fixedRouteId: number; name: string }>({
    mutationFn: async ({ fixedRouteId, name }) => {
      const res = await axios.patch<{ id: number; name: string; status: string; description: string | null; updated_at: string }>(
        `${BASE_PATH}/${fixedRouteId}`,
        { data: { name } }
      );
      return {
        id: res.data.id,
        name: res.data.name,
        status: res.data.status as 'draft' | 'active' | 'archived',
        description: res.data.description,
        vehicleCount: 0,
        updatedAt: res.data.updated_at,
      };
    },
  });

// 固定ルート削除
export const useDeleteFixedRouteMutation = () =>
  useMutation<void, Error, { fixedRouteId: number }>({
    mutationFn: async ({ fixedRouteId }) => {
      await axios.delete(`${BASE_PATH}/${fixedRouteId}`);
    },
  });

// 固定ルート反映: 対象日の既存配車枠へ fixed route を反映
//  - createOperations=false (呼び出し): reset + shift 調整のみ
//  - createOperations=true  (対応づけ):  上記に加え stop ↔ legRun マッチで operation 作成
export const useApplyFixedRouteMutation = () =>
  useMutation<
    ApplyFixedRouteResult,
    Error,
    { fixedRouteId: number; planningDate: string; createOperations?: boolean }
  >({
    mutationFn: async ({ fixedRouteId, planningDate, createOperations }) => {
      const res = await axios.post<{
        planning_date: string;
        fixed_route_id: number;
        applied: boolean;
        assigned_count: number;
        unassigned_count: number;
        no_delivery_count: number;
      }>(`${BASE_PATH}/${fixedRouteId}/applications`, {
        data: {
          planning_date: planningDate,
          create_operations: createOperations ?? false,
        },
      });
      return {
        planningDate: res.data.planning_date,
        fixedRouteId: res.data.fixed_route_id,
        applied: res.data.applied,
        assignedCount: res.data.assigned_count,
        unassignedCount: res.data.unassigned_count,
        noDeliveryCount: res.data.no_delivery_count,
      };
    },
  });
